import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, BusinessCard } from '../lib/api';
import { Mail, Phone, MapPin, Clock, Linkedin, Twitter, Github, Globe, Calendar, ExternalLink, Building2, Instagram, Youtube, MessageSquare } from 'lucide-react';
import { ServiceLinkPreview } from '../components/ServiceLinkPreview';
import eurekaLogo from '../assets/logo.svg';

// Theme styles - more professional, less AI-generated
const themeStyles = {
  MODERN: 'bg-white text-gray-900 border border-gray-200',
  CLASSIC: 'bg-slate-50 text-gray-900',
  BOLD: 'bg-gray-900 text-white',
  MINIMAL: 'bg-white text-gray-900',
};

const themeGradients = {
  MODERN: '',
  CLASSIC: '',
  BOLD: 'bg-gradient-to-br from-gray-900 to-gray-800',
  MINIMAL: '',
};

export function PublicCardPage() {
  const { slug } = useParams<{ slug: string }>();
  const [card, setCard] = useState<BusinessCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.getCardBySlug(slug)
      .then(setCard)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (notFound || !card) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Card Not Found</h1>
          <p className="text-gray-500 mt-2">This business card doesn't exist or has been disabled.</p>
        </div>
      </div>
    );
  }

  const visibility = card.visibilitySettings || {};
  const socialLinks = card.socialLinks || {};
  const serviceLinks = card.serviceLinks || [];

  const getSocialIcon = (platform: string) => {
    switch (platform) {
      case 'linkedin': return Linkedin;
      case 'twitter': return Twitter;
      case 'github': return Github;
      case 'website': return Globe;
      case 'calendar': return Calendar;
      case 'instagram': return Instagram;
      case 'youtube': return Youtube;
      case 'discord': return MessageSquare;
      default: return ExternalLink;
    }
  };

  const isDark = card.theme === 'BOLD';

  return (
    <div className={`min-h-screen py-12 px-4 ${themeGradients[card.theme]}`}>
      <div className="max-w-md mx-auto">
        {/* Card */}
        <div className={`${themeStyles[card.theme]} rounded-xl shadow-lg overflow-hidden border`}>
          {/* Header with Eureka branding - links to eurekalabo.com */}
          <a
            href="https://eurekalabo.com?utm_source=business-card"
            target="_blank"
            rel="noopener noreferrer"
            className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} px-6 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} block`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-gray-500" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Eureka Profile</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                <span className="text-xs text-gray-500">Available</span>
              </div>
            </div>
          </a>

          {/* Profile Section */}
          <div className="p-8 text-center">
            {card.photoUrl && (
              <div className="mx-auto w-28 h-28 rounded-full overflow-hidden border-4 shadow-sm mb-4 bg-white">
                <img src={card.photoUrl} alt={card.displayName} className="w-full h-full object-cover" />
              </div>
            )}
            <h1 className={`text-2xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{card.displayName}</h1>
            {card.title && <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{card.title}</p>}
            {card.bio && <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{card.bio}</p>}
          </div>

          {/* Contact Info */}
          <div className="px-6 pb-6 space-y-2">
            {visibility.email !== false && card.email && (
              <a
                href={`mailto:${card.email}`}
                className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} transition-colors group`}
              >
                <Mail className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} group-hover:text-blue-500`} />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} group-hover:text-blue-600`}>{card.email}</span>
              </a>
            )}

            {visibility.phone !== false && card.phone && (
              <a
                href={`tel:${card.phone}`}
                className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} transition-colors group`}
              >
                <Phone className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'} group-hover:text-green-500`} />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} group-hover:text-green-600`}>{card.phone}</span>
              </a>
            )}

            {visibility.officeLocation !== false && card.officeLocation && (
              <div className={`flex items-center gap-3 p-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`}>
                <MapPin className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{card.officeLocation}</span>
              </div>
            )}

            {visibility.workingHours !== false && card.workingHours && (
              <div className={`flex items-center gap-2 px-3 py-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Clock className="w-4 h-4" />
                <span className="text-xs">{card.workingHours}</span>
              </div>
            )}
          </div>

          {/* Social Links */}
          {visibility.socialLinks !== false && Object.keys(socialLinks).length > 0 && (
            <div className="px-6 pb-6">
              <div className="flex flex-wrap gap-2">
                {Object.entries(socialLinks).map(([platform, url]) => {
                  const Icon = getSocialIcon(platform);
                  return url ? (
                    <a
                      key={platform}
                      href={url as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'} transition-colors`}
                      title={platform}
                    >
                      <Icon className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                    </a>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Service Links */}
          {visibility.serviceLinks !== false && serviceLinks.length > 0 && (
            <div className="px-6 pb-6">
              <div className="space-y-2">
                {serviceLinks.map((link, index) => (
                  <ServiceLinkPreview
                    key={index}
                    title={link.title}
                    url={link.url}
                    icon={link.icon}
                    description={link.description}
                    imageUrl={link.imageUrl}
                    displayType={link.displayType}
                    isDark={isDark}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer with logo */}
        <div className="mt-6 text-center">
          <a
            href="https://eurekalabo.com?utm_source=business-card"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:opacity-80 transition-opacity"
          >
            <img src={eurekaLogo} alt="Eureka Labo" className="h-8 w-auto" />
          </a>
        </div>
      </div>
    </div>
  );
}
